// (B) Plain(플레인) ColorPicker 백엔드 — antd ColorPicker 대체. antd ColorPicker v5 트리거 DOM 이중클래스 미러링:
//   <div class="om-color-picker-trigger om-color-picker(+-active/-disabled)">
//     <div.om-color-picker-color-block><div.om-color-picker-color-block-inner style=background></div>
//     [showText && <div.om-color-picker-trigger-text>{hex 또는 커스텀}]
//   → 소비처 `.om-color-picker*` SCSS(2파일) 무변경 매칭. 패널(커스텀 HSV: SV 사각+hue 슬라이더+hex 입력)은
//   Radix Popover 로 포지셔닝. 값 계약은 hex 문자열(antd Color 객체 흡수). ninegqlUI 미사용·JS 쿼리 0.
//   presets(선택): antd ColorPicker presets 파리티 — 접이식 프리셋 색상 그룹(라벨+스와치, 활성 스와치 체크마크).
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import type { OmwColorPickerProps } from '../api/OmwColorPicker'
import { cx } from './cx'
import '../styles/omwColorPicker.css'

// ---- 색 변환(hex ↔ hsv) ----
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '')
  const full = h.length === 3 ? h.replace(/./g, (c) => c + c) : h
  const n = parseInt(full.slice(0, 6).padEnd(6, '0'), 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

function rgbToHex(r: number, g: number, b: number): string {
  const to = (v: number) =>
    Math.round(Math.max(0, Math.min(255, v)))
      .toString(16)
      .padStart(2, '0')
  return `#${to(r)}${to(g)}${to(b)}`
}

function rgbToHsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const d = max - min
  let h = 0
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6
    else if (max === gn) h = (bn - rn) / d + 2
    else h = (rn - gn) / d + 4
    h *= 60
    if (h < 0) h += 360
  }
  return { h, s: max === 0 ? 0 : d / max, v: max }
}

function hsvToRgb(h: number, s: number, v: number): { r: number; g: number; b: number } {
  const c = v * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = v - c
  let rp = 0
  let gp = 0
  let bp = 0
  if (h < 60) [rp, gp, bp] = [c, x, 0]
  else if (h < 120) [rp, gp, bp] = [x, c, 0]
  else if (h < 180) [rp, gp, bp] = [0, c, x]
  else if (h < 240) [rp, gp, bp] = [0, x, c]
  else if (h < 300) [rp, gp, bp] = [x, 0, c]
  else [rp, gp, bp] = [c, 0, x]
  return { r: (rp + m) * 255, g: (gp + m) * 255, b: (bp + m) * 255 }
}

function hexToHsv(hex: string) {
  const { r, g, b } = hexToRgb(hex)
  return rgbToHsv(r, g, b)
}

function hsvToHex(h: number, s: number, v: number) {
  const { r, g, b } = hsvToRgb(h, s, v)
  return rgbToHex(r, g, b)
}

const DEFAULT = '#1677ff'

// 프리셋 스와치 대비 체크마크 색(밝은 색 → 어두운 체크). ITU-R BT.601 luma.
function checkColorFor(hex: string): string {
  const { r, g, b } = hexToRgb(hex)
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? 'rgba(0, 0, 0, 88%)' : '#fff'
}

export function PlainColorPicker({
  value,
  onChange,
  disabled = false,
  showText,
  presets,
  className,
  style,
}: OmwColorPickerProps) {
  const isControlled = value !== undefined
  const [inner, setInner] = useState(DEFAULT)
  const current = (isControlled ? value : inner) || DEFAULT
  const [open, setOpen] = useState(false)
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({})
  const hsv = useMemo(() => hexToHsv(current), [current])

  const svRef = useRef<HTMLDivElement>(null)
  const hueRef = useRef<HTMLDivElement>(null)

  const commit = (hex: string) => {
    const up = hex.toUpperCase()
    if (!isControlled) setInner(up)
    onChange?.(up)
  }

  const setSV = (s: number, v: number) => commit(hsvToHex(hsv.h, s, v))
  const setHue = (h: number) => commit(hsvToHex(h, hsv.s, hsv.v))

  const dragSV = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled) return
    const el = svRef.current
    if (!el) return
    el.setPointerCapture(e.pointerId)
    const rect = el.getBoundingClientRect()
    const move = (clientX: number, clientY: number) => {
      const s = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
      const v = 1 - Math.max(0, Math.min(1, (clientY - rect.top) / rect.height))
      setSV(s, v)
    }
    move(e.clientX, e.clientY)
    const onMove = (ev: PointerEvent) => move(ev.clientX, ev.clientY)
    const onUp = () => {
      el.removeEventListener('pointermove', onMove)
      el.removeEventListener('pointerup', onUp)
    }
    el.addEventListener('pointermove', onMove)
    el.addEventListener('pointerup', onUp)
  }

  const dragHue = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled) return
    const el = hueRef.current
    if (!el) return
    el.setPointerCapture(e.pointerId)
    const rect = el.getBoundingClientRect()
    const move = (clientX: number) =>
      setHue(Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) * 360)
    move(e.clientX)
    const onMove = (ev: PointerEvent) => move(ev.clientX)
    const onUp = () => {
      el.removeEventListener('pointermove', onMove)
      el.removeEventListener('pointerup', onUp)
    }
    el.addEventListener('pointermove', onMove)
    el.addEventListener('pointerup', onUp)
  }

  const hueColor = hsvToHex(hsv.h, 1, 1)
  const text = typeof showText === 'function' ? showText() : current.toUpperCase()

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverPrimitive.Trigger asChild>
        <div
          role="button"
          aria-label="Color picker"
          aria-disabled={disabled || undefined}
          tabIndex={disabled ? -1 : 0}
          className={cx(
            'om-color-picker-trigger om-color-picker',
            open && 'om-color-picker-trigger-active',
            disabled && 'om-color-picker-trigger-disabled',
            className,
          )}
          style={style}
        >
          <div className="om-color-picker-color-block">
            <div className="om-color-picker-color-block-inner" style={{ background: current }} />
          </div>
          {showText && <div className="om-color-picker-trigger-text">{text}</div>}
        </div>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          className="om-color-picker-panel"
          aria-label="Color picker"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div
            ref={svRef}
            className="om-color-picker-sv"
            onPointerDown={dragSV}
            style={{ background: hueColor }}
          >
            <div className="om-color-picker-sv-white" />
            <div className="om-color-picker-sv-black" />
            <div
              className="om-color-picker-handle"
              style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
            />
          </div>
          <div ref={hueRef} className="om-color-picker-hue" onPointerDown={dragHue}>
            <div
              className="om-color-picker-handle om-color-picker-hue-handle"
              style={{ left: `${(hsv.h / 360) * 100}%` }}
            />
          </div>
          <div className="om-color-picker-footer">
            <div className="om-color-picker-preview" style={{ background: current }} />
            <input
              className="om-color-picker-hex"
              aria-label="Hex color"
              value={current.toUpperCase()}
              onChange={(e) => {
                const v = e.target.value
                if (/^#?[0-9a-fA-F]{0,6}$/.test(v)) {
                  const hex = v.startsWith('#') ? v : `#${v}`
                  if (/^#[0-9a-fA-F]{6}$/.test(hex)) commit(hex)
                  else if (!isControlled) setInner(hex)
                }
              }}
            />
          </div>
          {presets != null &&
            presets.length > 0 &&
            presets.map((preset, gi) => {
              const isOpen = collapsed[gi] ?? preset.defaultOpen ?? true
              return (
                <div className="om-color-picker-presets" key={preset.key ?? gi}>
                  <div className="om-color-picker-divider" />
                  <button
                    type="button"
                    className="om-color-picker-presets-label"
                    aria-expanded={isOpen}
                    onClick={() => setCollapsed((s) => ({ ...s, [gi]: !isOpen }))}
                  >
                    <span
                      className={cx(
                        'om-color-picker-presets-arrow',
                        isOpen && 'om-color-picker-presets-arrow-open',
                      )}
                    />
                    {preset.label}
                  </button>
                  {isOpen && (
                    <div className="om-color-picker-presets-items">
                      {preset.colors.map((c) => {
                        const active = c.toUpperCase() === current.toUpperCase()
                        return (
                          <div
                            key={c}
                            role="button"
                            tabIndex={disabled ? -1 : 0}
                            aria-label={c}
                            className={cx(
                              'om-color-picker-presets-color',
                              active && 'om-color-picker-presets-color-active',
                            )}
                            style={{ background: c }}
                            onClick={() => !disabled && commit(c)}
                          >
                            {active && (
                              <span
                                className="om-color-picker-presets-check"
                                style={{ color: checkColorFor(c) }}
                              />
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
